import * as React from "react";
import { graphql } from "gatsby";
import { Trans, useTranslation } from "react-i18next";
import LandingLayout from "../../components/page-layout/LandingLayout";
import TextHero from "../../components/hero/TextHero";
import { LandingCta } from "../../components/cta/LandingCta";
import ClientsSections from "../../components/sections/ClientsSection";
import { Testimonials } from "../../components/sections/Testimonials";
import { Faq } from "../../components/FAQ";
import BodyText from "../../components/typography/BodyText";
import TechIcon from "../../components/icons/TechIcon";
import SideGridBlock from "../../components/grid-block/SideGridBlock";
import { FeaturesGrid6 } from "../../components/features-grid/FeaturesGrid6";
import { Comparison } from "../../components/Comparison";
import { UseCases } from "../../components/sections/UseCases";
import TextCta from "../../components/cta/TextCta";
import { useScheduleCallUrl } from "../../hooks/useScheduleCallUrl";
import SideNumberedList from "../../components/grid-block/SideNumberedList";
import { FeaturesGrid3 } from "../../components/features-grid/FeaturesGrid3";
import { useServiceTree } from "../../hooks/useServiceTree";
import Typewriter from "typewriter-effect";

const LongLanding = () => {
  const { t } = useTranslation();
  const callUrl = useScheduleCallUrl();
  const services = useServiceTree();

  return (
    <LandingLayout darkHeader>
      <TextHero
        dark
        extraWide
        center={false}
        tag="astrorei"
        topCta={
          <p className="text-lg text-gray-200">
            {t("landing:digital.hero.tag")}
          </p>
        }
        title={
          <Trans
            i18nKey="landing:digital.hero.title_tw"
            components={{
              light: <span className="font-normal" />,
              typewriter: (
                <Typewriter
                  options={{
                    strings: t("landing:digital.hero.typewriter", {
                      returnObjects: true,
                    }),
                    autoStart: true,
                    loop: true,
                    wrapperClassName: "text-primary",
                    cursorClassName: "text-primary",
                    delay: 75,
                  }}
                  component="span"
                />
              ),
            }}
          />
        }
        description={t("landing:digital.hero.description")}
        cta={
          <LandingCta
            label={t("landing:digital.hero.cta.button")}
            theme="dark"
            className="mt-8"
          />
        }
      />
      <ClientsSections border={false} />

      <UseCases
        tag={t(`index:useCase.tag`)}
        title={t(`index:useCase.title`)}
        description={t(`index:useCase.description`)}
        centerTitle
        target="_blank"
      />

      <FeaturesGrid3
        theme="gray"
        tag={t(`landing:digital.solutions.tag`)}
        title={t(`landing:digital.solutions.title`)}
        elements={services
          .find((s) => s.id === "customSoftwareSolutions")!
          .solutions.map((solution) => ({
            name: t(`solutions:${solution.id}.name`),
            icon: (
              <TechIcon
                size="xl"
                icon={solution.icon}
                className="text-gray-900 overflow-hidden"
              />
            ),
          }))}
      />

      <TextCta
        tag={t(`landing:digital.cta.tag`)}
        title={t(`landing:digital.cta.title`)}
        cta={
          <LandingCta
            center
            theme="dark"
            label={t("landing:digital.cta.button")}
            notes={
              <Trans
                i18nKey="landing:digital.cta.notes"
                components={{
                  a: (
                    <a
                      href={callUrl}
                      target="_blank"
                      className="text-primary underline"
                    />
                  ),
                }}
              />
            }
          />
        }
      />

      <Comparison
        pros={t(`landing:digital.comparison.pros`, {
          returnObjects: true,
        })}
        cons={t(`landing:digital.comparison.cons`, {
          returnObjects: true,
        })}
      />

      <FeaturesGrid6
        half
        noMargin="top"
        tag={t(`landing:digital.whyAstrorei.tag`)}
        title={t(`landing:digital.whyAstrorei.title`)}
        elements={t(`landing:digital.whyAstrorei.elements`, {
          returnObjects: true,
        })}
      />

      <SideNumberedList
        theme="dark"
        tag={t(`landing:digital.method.tag`)}
        title={t(`landing:digital.method.title`)}
        description={<Trans i18nKey="landing:digital.method.description" />}
        items={t("landing:digital.method.items", { returnObjects: true }) ?? []}
      >
        <>
          <BodyText dark className="text-center lg:text-left mt-14">
            <Trans i18nKey="landing:digital.method.cta.description" />
          </BodyText>
          <LandingCta
            className="items-center lg:items-start"
            label={t("landing:digital.method.cta.button")}
            notes={
              <Trans
                i18nKey="landing:digital.method.cta.notes"
                components={{
                  a: (
                    <a
                      href={callUrl}
                      target="_blank"
                      className="text-primary underline"
                    />
                  ),
                }}
              />
            }
            theme="dark"
          />
        </>
      </SideNumberedList>

      <SideGridBlock
        theme="gray"
        mobileMultiCol
        tag={t(`landing:digital.tools.tag`)}
        title={t(`landing:digital.tools.title`)}
        description={<Trans i18nKey="landing:digital.tools.description" />}
        blocks={[
          {
            name: "React",
            icon: "react",
          },
          {
            name: "Node.js",
            icon: "nodeJS",
          },
          {
            name: "AWS",
            icon: "aws",
          },
          {
            name: "AI",
            icon: "ai",
          },
          {
            name: "DevOps",
            icon: "devops",
          },
          {
            name: ".NET",
            icon: "dotnet",
          },
          {
            name: "Rust",
            icon: "rust",
          },
          {
            name: "Flutter",
            icon: "flutter",
          },
        ].map((tech) => (
          <div className="flex items-center justify-center p-6">
            <TechIcon
              icon={tech.icon}
              size="xl"
              caption={tech.name}
              className="text-gray-800 z-10"
            />
          </div>
        ))}
      />
      <Testimonials />
      <Faq
        theme="indigo"
        bgTagText={t("landing:digital.faq.bgTagText")}
        tag={t("landing:digital.faq.tag")}
        title={t("landing:digital.faq.title")}
        faqs={t(`landing:digital.faq.faqs`, { returnObjects: true })}
      />
    </LandingLayout>
  );
};

export default LongLanding;

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
